// @flow

// Flow doesn't play well with constants:
// See https://github.com/facebook/flow/issues/2377
export const ENTRY_TYPE: 'entry' = 'entry';
export const MESSAGE_ACTION_TYPE: 'message-action' = 'message-action';
export const DELAY_TYPE: 'delay' = 'delay';
export const EVENT_LISTENER_TYPE: 'event-listener' = 'event-listener';
export const MESSAGE_TYPE: 'message' = 'message';
export const EXIT_TYPE: 'exit' = 'exit';
export const ADD_NODE_TYPE: 'add_node' = 'add_node';
export const LABEL_NODE_TYPE: 'label_node' = 'label_node';

export const EVENT_SUBTYPE: 'event' = 'event';
export const SESSION_SUBTYPE: 'session' = 'session';

export const VERSION_STATE_DRAFT: 'draft' = 'draft';
export const VERSION_STATE_OPEN: 'open' = 'open';
export const VERSION_STATE_CLOSED: 'closed' = 'closed';
export const VERSION_STATE_TERMINATED: 'terminated' = 'terminated';

export const MESSAGE_TYPE_PUSH: 'push' = 'push';

export const PUSH_SUBTYPE: 'push' = 'push';
export const INAPP_SUBTYPE: 'inapp' = 'inapp';
export const EMAIL_SUBTYPE: 'email' = 'email';

export const MESSAGE_ACTION_SENT: 'sent' = 'sent';
export const MESSAGE_ACTION_UNREACHABLE: 'unreachable' = 'unreachable';

export const ERROR_INVALID_TYPE: 'invalid_type' = 'invalid_type';
export const ERROR_VALUE_MISSING: 'value_missing' = 'value_missing';
export const ERROR_INVALID_REFERENCE: 'invalid_reference' = 'invalid_reference';

export const GENERIC_ERROR_MESSAGE =
  'There was an error while trying to process this request.  Please try again later, or contact support.';

export const VERSION_STATE_MAP = {
  [VERSION_STATE_OPEN]: 'Active',
  [VERSION_STATE_TERMINATED]: 'Disabled',
  [VERSION_STATE_CLOSED]: 'Deactivated',
  [VERSION_STATE_DRAFT]: 'Draft'
};

export const defaultConfig = {
  config: {
    reenterable: true,
    interval_minutes: 60,
    max_times: 11
  }
};

export type Creative = {
  id: number,
  orchestration_version_id: number,
  creative_type: string,
  created_by: string,
  created_at: string,
  updated_by: string | null,
  updated_at: string | null,
  data: { [any]: any }
};

export type Event = {
  name: string,
  olap_id: string
};

export type EntryNode = {
  node_type: typeof ENTRY_TYPE,
  events: Array<Event>,
  event_mapping_id: number,
  next: string
};

export type EventListenerNode = {
  node_type: typeof EVENT_LISTENER_TYPE,
  display_name: string,
  events: Array<Event>,
  expiration_minutes: number,
  event_mapping_id: number,
  next: string
};

export type DelayNode = {
  node_type: typeof DELAY_TYPE,
  next: string,
  duration_minutes: number
};

export type MessageNode = {
  node_type: typeof MESSAGE_TYPE,
  message_type: typeof MESSAGE_TYPE_PUSH,
  next: string,
  creative_ids: Array<number>
};

export type ImplicitMessageActionNode = {
  node_type: typeof MESSAGE_ACTION_TYPE,
  action: typeof MESSAGE_ACTION_SENT | typeof MESSAGE_ACTION_UNREACHABLE,
  next: string
};

export type ExitNode = {
  node_type: typeof EXIT_TYPE
};

export type Node =
  | EntryNode
  | EventListenerNode
  | DelayNode
  | MessageNode
  | ImplicitMessageActionNode
  | ExitNode;

export type VersionConfig = {
  reenterable: boolean,
  max_times: number | null,
  interval_minutes: number | null
};

export type VersionState =
  | typeof VERSION_STATE_DRAFT
  | typeof VERSION_STATE_OPEN
  | typeof VERSION_STATE_CLOSED
  | typeof VERSION_STATE_TERMINATED;

export type OrchestrationVersion = {
  id: number,
  orchestration_id: number,
  created_by: string,
  created_at: string,
  updated_by: string | null,
  updated_at: string | null,
  version_number: number,
  schema_version_number: number,
  app_group_id: number,
  nodes: { [number]: Node },
  state: VersionState,
  config: VersionConfig,
  notes: string,
  apps: Array<{ app_uuid: string, olap_id: number }>
};

export const isEditable = (version: OrchestrationVersion) =>
  version.state === VERSION_STATE_DRAFT;

export const hasActiveVersion = (versions: Array<OrchestrationVersion>) =>
  versions.some(version => !isEditable(version));

export type Orchestration = {
  id: number,
  org_id: number,
  name: string,
  created_by: string,
  created_at: string,
  updated_by: string | null,
  updated_at: string | null
};

export type Response =
  | Array<Orchestration>
  | Orchestration
  | Array<OrchestrationVersion>
  | OrchestrationVersion
  | Array<Creative>
  | Creative;

export type InitialState = {
  orchestrations: Array<Orchestration>,
  orchestrationsById: { [string]: Orchestration },
  versionsByOrchestrationId: { [string]: { [string]: OrchestrationVersion } },
  creatives: Array<Creative>,
  creativesById: { [string]: Creative }
};

export type Action = {
  type: string,
  status?: number,
  payload: { [any]: any },
  response: Response
};

export type Error = {
  error: string,
  field_name: string,
  expected_type: string
};

export type ErrorResponse = {
  message: string,
  errors?: Array<Error>,
  app_errors?: {
    [string]: Array<Error>
  },
  node_errors?: {
    [string]: Array<Error>
  },
  unknown_errors?: {
    [string]: Array<Error>
  }
};

export type GeneralOperator = 'IS_NULL' | 'IS_NOT_NULL';
export type StringOperator = 'EQ' | 'NEQ' | 'IN' | 'NOT_IN';
export type NumberOperator = 'LT' | 'GT' | 'LTEQ' | 'GTEQ';

export type Operator = StringOperator | NumberOperator | GeneralOperator;

export type AttributeCondition = {
  property: string,
  op: Operator,
  type: 'NUMBER' | 'STRING',
  value?: string,
  values?: Array<string>
};

export type OperatorOptionsType = {
  label: string,
  id: Operator,
  multiple: boolean
};



// WEBPACK FOOTER //
// ./src/constants/ApiTypes.js